import { useQuery } from 'react-query';

import { QueryEntity } from '../types';
import useGetFetchConfig from '../useGetFetchConfig';
import { APIBaseChronos } from '@/api/hosts';

type RelativityDocumentsResponse = {
  documents: Array<{
    artifactId: number;
    name: string;
  }>;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
};

type RelativityDocumentCountResponse = {
  count: number;
};

const useGetRelativityDocumentCount = (workspaceId: number, listId: number, enabled = true) => {
  const { fetchConfigGET } = useGetFetchConfig();

  const getRelativityDocumentCount = async (): Promise<RelativityDocumentCountResponse> => {
    if (!workspaceId || !listId) {
      throw new Error('Workspace ID and List ID are required');
    }

    // Fetch with page size 0 to get just the count
    const searchParams = new URLSearchParams();
    searchParams.append('page', '1');
    searchParams.append('pageSize', '0');

    const url = `${APIBaseChronos}/api/relativity/workspaces/${workspaceId}/lists/${listId}/documents?${searchParams.toString()}`;
    const response = await fetch(url, fetchConfigGET);

    if (!response.ok) {
      throw new Error(`Failed to fetch document count: ${response.status} ${response.statusText}`);
    }

    const data: RelativityDocumentsResponse = await response.json();
    return { count: data.totalCount };
  };

  return useQuery([QueryEntity.RelativityDocumentCount, { workspaceId, listId }], getRelativityDocumentCount, {
    enabled: enabled && !!workspaceId && !!listId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - document counts don't change frequently
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('401')) {
        return false;
      }
      if (error instanceof Error && error.message.includes('403')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export default useGetRelativityDocumentCount;
